import type { Community, Me } from "@packages/sdk";
import { CommunityType } from "@packages/sdk";
import type {
  BasicIconProps,
  SidebarProps,
  SidebarTabProps,
} from "@packages/ui";
import {
  ActivityIcon,
  AnnouncementIcon,
  CommunityAltIcon,
  FriendsIcon,
  GivingIcon,
  InfoIcon,
  PermissionIcon,
  ReportIcon,
  SettingsAltIcon,
  UpwardTrendIcon,
} from "@packages/ui";
import {
  CalendarIcon,
  Library2Icon,
} from "@packages/ui/src/components/_base/icons/Icon/GeneralIcons";
import type { JSX } from "react";

type PADTAB = Pick<SidebarTabProps, "actionRight"> & {
  sidebarState: SidebarProps["state"];
  path: string;
  Icon: (props: BasicIconProps) => JSX.Element;
  getHidden?: ({
    community,
    user,
  }: {
    community: Community;
    user?: Me;
  }) => boolean;
} & (
    | {
        message: string;
        getMessage?: never;
      }
    | {
        message?: never;
        getMessage: (community: Community) => string;
      }
  );

export const PAD_TABS: (PADTAB & {
  subTabs?: PADTAB[];
})[] = [
  {
    getHidden: ({ community }) =>
      ![
        CommunityType.parish,
        CommunityType.basic,
        CommunityType.small_group,
        CommunityType.network,
        CommunityType.school,
        CommunityType.religious_ed_program,
      ].includes(community.type) || !community.is_admin,
    Icon: UpwardTrendIcon,
    message: "web_dashboard",
    path: "dashboard",
    sidebarState: "expanded",
  },
  {
    Icon: ActivityIcon,
    getHidden: ({ community }) =>
      ![
        CommunityType.parish,
        CommunityType.basic,
        CommunityType.small_group,
      ].includes(community.type),
    message: "intentions_activity",
    path: "activity",
    sidebarState: "expanded",
  },
  {
    Icon: Library2Icon,
    getHidden: ({ community }) =>
      ![
        CommunityType.parish,
        CommunityType.basic,
        CommunityType.small_group,
      ].includes(community.type),
    message: "me_tabs_title_library",
    path: "library",
    sidebarState: "expanded",
  },
  {
    Icon: CalendarIcon,
    getHidden: ({ community }) =>
      ![CommunityType.parish].includes(community.type),
    message: "community_events_title",
    path: "events",
    sidebarState: "expanded",
  },
  {
    Icon: GivingIcon,
    getHidden: ({ community }) =>
      ![
        CommunityType.parish,
        CommunityType.basic,
        CommunityType.network,
      ].includes(community.type),
    message: "giving_title",
    path: "giving",
    sidebarState: "expanded",
  },
  {
    Icon: CommunityAltIcon,
    getHidden: ({ community }) =>
      ![
        CommunityType.parish,
        CommunityType.basic,
        CommunityType.network,
      ].includes(community.type),
    message: "community_detail_groups_screen_title",
    path: "groups",
    sidebarState: "closed",
  },
  {
    Icon: CommunityAltIcon,
    getHidden: ({ community }) =>
      ![CommunityType.school, CommunityType.religious_ed_program].includes(
        community.type,
      ),
    message: "general_word_classes",
    path: "classes",
    sidebarState: "expanded",
  },
  {
    Icon: FriendsIcon,
    getHidden: ({ community }) =>
      ![
        CommunityType.parish,
        CommunityType.basic,
        CommunityType.school,
        CommunityType.small_group,
        CommunityType.religious_ed_program,
      ].includes(community.type) || !community.is_admin,
    getMessage: (community) =>
      community?.type === CommunityType.school
        ? "admin_dash_roster"
        : "community_settings_members",
    path: "members",
    sidebarState: "expanded",
  },
  {
    Icon: AnnouncementIcon,
    getHidden: ({ community }) =>
      ![
        CommunityType.parish,
        CommunityType.basic,
        CommunityType.network,
      ].includes(community.type),
    message: "dashboard_broadcast",
    path: "broadcast",
    sidebarState: "expanded",
  },
  {
    Icon: ReportIcon,
    getHidden: ({ community }) =>
      ![
        CommunityType.parish,
        CommunityType.basic,
        CommunityType.small_group,
      ].includes(community.type),
    message: "parish_admin_dashboard_navigation_reported",
    path: "reported",
    sidebarState: "expanded",
  },
  {
    Icon: SettingsAltIcon,
    getHidden: ({ community }) =>
      ![
        CommunityType.parish,
        CommunityType.basic,
        CommunityType.network,
        CommunityType.small_group,
      ].includes(community.type),
    message: "settings_title",
    path: "settings",
    sidebarState: "closed",
    subTabs: [
      {
        Icon: InfoIcon,
        path: "group-info",
        message: "community_settings_group_info",
        sidebarState: "expanded",
      },
      {
        getHidden: ({ community }) =>
          ![
            CommunityType.parish,
            CommunityType.basic,
            CommunityType.small_group,
          ].includes(community.type),
        Icon: PermissionIcon,
        path: "permissions",
        message: "community_settings_permissions",
        sidebarState: "expanded",
      },
    ],
  },
];

export const padTabsPaths = PAD_TABS.map(({ path }) => path);

export const PAD_SUPPORT_EMAIL = "parishes@hallow.app";

export const PAD_COUNTRY_OPTIONS = [
  {
    value: "US",
    label: "United States",
  },
  {
    value: "BR",
    label: "Brazil",
  },
  {
    value: "DE",
    label: "Germany",
  },
  {
    value: "MX",
    label: "Mexico",
  },
  {
    value: "PH",
    label: "Philippines",
  },
  {
    value: "GB",
    label: "United Kingdom",
  },
];

export const PAD_PATH_TO_SCREEN_NAME_MAP = {
  "/admin/*/dashboard/**": "pad_dashboard",
  "/admin/*/activity/**": "pad_activity",
  "/admin/*/giving/**": "pad_giving",
  "/admin/*/groups/**": "pad_groups",
  "/admin/*/members/**": "pad_members",
  "/admin/*/broadcast/**": "pad_broadcast",
  "/admin/*/reported/**": "pad_reported",
  "/admin/*/settings/**": "pad_settings",
  "/admin/*/library/**": "pad_library",
  "/admin/*/events": "events_home",
  "/admin/*/events/**": "pad_events",
};

export const PAD_MOBILE_VIEW_ENABLED_PATHS = [
  "dashboard",
  "settings",
  "members",
  "giving",
  "groups",
  "broadcast",
  "library",
  "events",
];

export const PAD_MEMBER_IMPORT_COLUMNS = ["name", "last_name", "email"];
export const PAD_STUDENT_USERNAME_IMPORT_COLUMNS = [
  "name",
  "last_name",
  "username",
];
export const PAD_KID_EMAIL_IMPORT_COLUMNS = [
  "name",
  "last_name",
  "email",
  "parent_email",
];
export const PAD_KID_USERNAME_IMPORT_COLUMNS = [
  "name",
  "last_name",
  "username",
  "parent_email",
];
